import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { nanoid } from "nanoid";
import { Form, Button, Drawer, AutoComplete, Tag, Typography } from "antd";
import { CloseOutlined } from "@ant-design/icons";
import Disclaimer from "Shared/Disclaimer";
import { Tokens } from "Shared/Tokens";
import * as assetAction from "store/Actions/asset";
import { mpTapAddCoin } from "Shared/Mixpanel/Assets";
import { getCoinsList } from "utils/CoinGecko";

const { Title, Text } = Typography;

const POPULAR_COINS = [
    { id: "bitcoin", name: "Bitcoin", symbol: "BTC" },
    { id: "ethereum", name: "Ethereum", symbol: "ETH" },
    { id: "solana", name: "Solana", symbol: "SOL" },
    { id: "binancecoin", name: "BNB", symbol: "BNB" },
    { id: "ripple", name: "XRP", symbol: "XRP" },
];

export const normalizeAndDedupeCoinList = (coins = []) => {
    if (!Array.isArray(coins)) return [];
    const sorted = [...coins]
        .filter((c) => c && c.name && c.id)
        .sort((a, b) => String(a.id).length - String(b.id).length);

    const byName = new Map();
    sorted.forEach((coin) => {
        const cleanName = String(coin.name).trim();
        if (!cleanName) return;
        const nameKey = cleanName.toLowerCase();
        if (!byName.has(nameKey)) {
            byName.set(nameKey, {
                id: coin.id,
                name: cleanName,
                symbol: String(coin.symbol || "").trim(),
                value: cleanName,
                size: cleanName.length,
            });
        }
    });
    return Array.from(byName.values());
};

export const searchCoins = (coinList = [], searchText = "", limit = 50) => {
    const query = String(searchText || "")
        .trim()
        .toLowerCase();
    if (!query || !Array.isArray(coinList) || coinList.length === 0) {
        return [];
    }

    const matches = coinList.filter((coin) => {
        const nameLower = String(coin.name || "").toLowerCase();
        const symbolLower = String(coin.symbol || "").toLowerCase();
        return nameLower.includes(query) || symbolLower.includes(query);
    });

    matches.sort((a, b) => {
        const aName = String(a.name || "").toLowerCase();
        const bName = String(b.name || "").toLowerCase();
        const aSym = String(a.symbol || "").toLowerCase();
        const bSym = String(b.symbol || "").toLowerCase();

        const aExact = aName === query || aSym === query ? 0 : 1;
        const bExact = bName === query || bSym === query ? 0 : 1;
        if (aExact !== bExact) return aExact - bExact;

        const aNameStarts = aName.startsWith(query) ? 0 : 1;
        const bNameStarts = bName.startsWith(query) ? 0 : 1;
        if (aNameStarts !== bNameStarts) return aNameStarts - bNameStarts;

        const aSymStarts = aSym.startsWith(query) ? 0 : 1;
        const bSymStarts = bSym.startsWith(query) ? 0 : 1;
        if (aSymStarts !== bSymStarts) return aSymStarts - bSymStarts;

        if (aName.length !== bName.length) return aName.length - bName.length;
        return String(a.id || "").length - String(b.id || "").length;
    });

    return matches.slice(0, limit);
};

const AddAsset = (props) => {
    const { visible = false, onClose = () => {} } = props;
    const dispatch = useDispatch();
    const [form] = Form.useForm();
    const selectedPortfolio = useSelector(
        (state) => state.portfolios.selectedPortfolio
    );
    const cachedCoinList = useSelector((state) => state.portfolios.coinList);
    const userInfo = useSelector((state) => state.users?.userInfo);
    const [disabled, setDisabled] = useState(true);
    const [asset, setAsset] = useState(null);
    const [searchValue, setSearchValue] = useState("");
    const [coinList, setCoinList] = useState([]);
    const [options, setOptions] = useState([]);
    const [isMobile, setIsMobile] = useState(
        typeof window !== "undefined" ? window.innerWidth < 768 : false
    );

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 768);
        };
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    const loadCoinList = async () => {
        const coins = await getCoinsList();
        const deduped = normalizeAndDedupeCoinList(coins);
        setCoinList(deduped);
        return deduped;
    };

    useEffect(() => {
        if (Array.isArray(cachedCoinList) && cachedCoinList.length > 0) {
            setCoinList(normalizeAndDedupeCoinList(cachedCoinList));
        } else {
            loadCoinList();
        }
        // eslint-disable-next-line
    }, [cachedCoinList]);

    useEffect(() => {
        if (visible && coinList.length === 0) {
            loadCoinList();
        }
        if (!visible) {
            setSearchValue("");
            setAsset(null);
            setDisabled(true);
            setOptions([]);
            form.resetFields();
        }
        // eslint-disable-next-line
    }, [visible]);

    const buildOptionItems = (matchedCoins) =>
        matchedCoins.map((coin) => ({
            key: coin.id,
            value: coin.name,
            coin,
            label: (
                <div
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "8px",
                        width: "100%",
                    }}
                >
                    <span
                        style={{
                            color: Tokens.textPrimary,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                        }}
                    >
                        {coin.name}
                    </span>
                    {coin.symbol && (
                        <span
                            style={{
                                color: Tokens.textMuted,
                                fontSize: "11px",
                                fontFamily: Tokens.fontMono,
                                textTransform: "uppercase",
                                flexShrink: 0,
                            }}
                        >
                            {coin.symbol.toUpperCase()}
                        </span>
                    )}
                </div>
            ),
        }));

    const resolveSelectedCoin = (text, listToSearch) => {
        const q = String(text || "")
            .trim()
            .toLowerCase();
        if (!q) return null;
        return (
            listToSearch.find(
                (c) => String(c.name || "").toLowerCase() === q
            ) ||
            listToSearch.find(
                (c) => String(c.symbol || "").toLowerCase() === q
            ) ||
            null
        );
    };

    const handleSelect = (coinName, option) => {
        const selectedCoin =
            option?.coin || resolveSelectedCoin(coinName, coinList);
        setSearchValue(coinName);
        if (selectedCoin && selectedCoin.name) {
            setAsset(selectedCoin);
            setDisabled(false);
        } else {
            setAsset(null);
            setDisabled(true);
        }
    };

    const handleSearch = async (searchText) => {
        setSearchValue(searchText);
        let activeList = coinList;
        if (!activeList || activeList.length === 0) {
            activeList = await loadCoinList();
        }
        const matched = searchCoins(activeList, searchText, 50);
        setOptions(buildOptionItems(matched));

        const exactCoin = resolveSelectedCoin(searchText, activeList);
        if (exactCoin && exactCoin.name) {
            setAsset(exactCoin);
            setDisabled(false);
        } else {
            setAsset(null);
            setDisabled(true);
        }
    };

    const handleQuickSelect = (popularCoin) => {
        const fromList = coinList.find((c) => c.id === popularCoin.id) ||
            resolveSelectedCoin(popularCoin.symbol, coinList) ||
            resolveSelectedCoin(popularCoin.name, coinList) || {
                id: popularCoin.id,
                name: popularCoin.name,
                symbol: popularCoin.symbol.toLowerCase(),
                value: popularCoin.name,
                size: popularCoin.name.length,
            };
        setSearchValue(fromList.name);
        setAsset(fromList);
        setDisabled(false);
    };

    const onFinish = () => {
        if (!asset || !asset.name) return;
        mpTapAddCoin({ name: asset.name });
        dispatch(
            assetAction.add({
                name: asset.name,
                id: asset.id,
                symbol: asset.symbol,
                avatar: "",
                portfolioId: selectedPortfolio.uid,
                uid: nanoid(),
                transactions: [],
                portfolio: {},
                createdAt: Date.now(),
            })
        );
        onClose();
    };

    return (
        <div>
            <Drawer
                title={
                    <Title
                        level={4}
                        style={{
                            margin: 0,
                            color: Tokens.textPrimary,
                            fontWeight: 500,
                            fontSize: "18px",
                        }}
                    >
                        Add coin
                    </Title>
                }
                placement={isMobile ? "bottom" : "right"}
                closable
                push={false}
                closeIcon={
                    <CloseOutlined style={{ color: Tokens.textSecondary }} />
                }
                onClose={onClose}
                open={visible}
                width={isMobile ? "100%" : 460}
                height={isMobile ? "75%" : "100%"}
                maskClosable
                maskStyle={{ backgroundColor: "rgba(0, 0, 0, 0.75)" }}
                headerStyle={{
                    backgroundColor: Tokens.bg,
                    borderBottom: `1px solid ${Tokens.hairline}`,
                }}
                bodyStyle={{
                    backgroundColor: Tokens.bg,
                    padding: "24px 16px",
                }}
                footerStyle={{
                    backgroundColor: Tokens.card,
                    borderTop: `1px solid ${Tokens.hairline}`,
                    padding: "12px 16px",
                }}
            >
                <div
                    style={{
                        maxWidth: Tokens.containerMaxWidth,
                        margin: "0 auto",
                        width: "100%",
                    }}
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={onFinish}
                        name="add-asset"
                        autoComplete="off"
                    >
                        <Form.Item
                            label={
                                <Text style={{ color: Tokens.textSecondary }}>
                                    Coin name or symbol
                                </Text>
                            }
                        >
                            <AutoComplete
                                value={searchValue}
                                options={options}
                                style={{ width: "100%" }}
                                onSelect={handleSelect}
                                onSearch={handleSearch}
                                onChange={(val) => {
                                    setSearchValue(val);
                                    const exact = resolveSelectedCoin(
                                        val,
                                        coinList
                                    );
                                    if (exact && exact.name) {
                                        setAsset(exact);
                                        setDisabled(false);
                                    } else {
                                        setAsset(null);
                                        setDisabled(true);
                                    }
                                }}
                                placeholder="Search by name or ticker (e.g. Bitcoin, BTC, Solana)"
                                notFoundContent={
                                    searchValue.trim() ? (
                                        <span
                                            style={{
                                                color: Tokens.textMuted,
                                                fontSize: "13px",
                                            }}
                                        >
                                            No matching coins found
                                        </span>
                                    ) : null
                                }
                            />
                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                    flexWrap: "wrap",
                                    marginTop: "10px",
                                }}
                            >
                                <span
                                    style={{
                                        color: Tokens.textMuted,
                                        fontSize: "11px",
                                        marginRight: "2px",
                                    }}
                                >
                                    Popular:
                                </span>
                                {POPULAR_COINS.map((item) => {
                                    const isSelected =
                                        asset?.id === item.id ||
                                        String(
                                            asset?.symbol || ""
                                        ).toUpperCase() === item.symbol;
                                    return (
                                        <Tag
                                            key={item.symbol}
                                            onClick={() =>
                                                handleQuickSelect(item)
                                            }
                                            style={{
                                                margin: 0,
                                                cursor: "pointer",
                                                backgroundColor: isSelected
                                                    ? Tokens.accentSubtle
                                                    : Tokens.raised,
                                                color: isSelected
                                                    ? Tokens.accent
                                                    : Tokens.textSecondary,
                                                border: `1px solid ${
                                                    isSelected
                                                        ? Tokens.accentBorder
                                                        : Tokens.hairline
                                                }`,
                                                borderRadius: Tokens.radiusTag,
                                                fontFamily: Tokens.fontMono,
                                                fontSize: "11px",
                                                padding: "2px 8px",
                                                userSelect: "none",
                                            }}
                                        >
                                            {item.symbol}
                                        </Tag>
                                    );
                                })}
                            </div>
                        </Form.Item>
                        <Form.Item>
                            <Button
                                disabled={disabled}
                                type="primary"
                                htmlType="submit"
                                block
                            >
                                {asset?.name ? `Add ${asset.name}` : "Add"}
                            </Button>
                        </Form.Item>
                        {!userInfo && <Disclaimer />}
                    </Form>
                </div>
            </Drawer>
        </div>
    );
};

export default AddAsset;
